/**
 * Firebase Authentication Service
 * Handles user authentication with Firebase Auth
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  User as FirebaseUser,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import type { User } from '../types';

const USERS_COLLECTION = 'users';

/**
 * Check if a username is already taken
 */
export async function isUsernameTaken(username: string, excludeUserId?: string): Promise<boolean> {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('username', '==', username));
    const querySnapshot = await getDocs(q);
    
    // If excludeUserId is provided, check if the username belongs to a different user
    if (excludeUserId) {
      for (const doc of querySnapshot.docs) {
        if (doc.id !== excludeUserId) {
          return true; // Username is taken by another user
        }
      }
      return false; // Username is only used by the current user
    }
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking username:', error);
    return false; // On error, allow the username
  }
}

/**
 * Create a new user account
 */
export async function signUp(
  email: string,
  password: string,
  username: string
): Promise<User> {
  try {
    // Check if username is already taken
    const usernameTaken = await isUsernameTaken(username);
    if (usernameTaken) {
      throw new Error('This username is already taken. Please choose a different username.');
    }

    // Create auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Update display name
    await updateProfile(firebaseUser, {
      displayName: username,
    });

    // Create user document in Firestore
    const user: User = {
      id: firebaseUser.uid,
      username: username,
      email: email,
      displayName: username,
      createdAt: Date.now(),
      events: [],
    };

    await setDoc(doc(db, USERS_COLLECTION, firebaseUser.uid), {
      username: username,
      email: email,
      displayName: username,
      createdAt: Date.now(),
    });

    console.log('User created successfully:', user);
    return user;
  } catch (error: any) {
    console.error('Error creating user:', error);
    throw new Error(error.message || 'Failed to create account');
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<User> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, firebaseUser.uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const user: User = {
        id: firebaseUser.uid,
        username: userData.username || firebaseUser.displayName || 'User',
        email: firebaseUser.email || email,
        displayName: userData.displayName || firebaseUser.displayName,
        createdAt: userData.createdAt || Date.now(),
        events: [],
      };
      console.log('User signed in:', user);
      return user;
    } else {
      // Create user document if it doesn't exist
      const user: User = {
        id: firebaseUser.uid,
        username: firebaseUser.displayName || email.split('@')[0],
        email: firebaseUser.email ?? email,
        displayName: firebaseUser.displayName ?? undefined,
        createdAt: Date.now(),
        events: [],
      };
      
      await setDoc(doc(db, USERS_COLLECTION, firebaseUser.uid), {
        username: user.username,
        email: user.email,
        displayName: user.displayName,
        createdAt: user.createdAt,
      });
      
      return user;
    }
  } catch (error: any) {
    console.error('Error signing in:', error);
    throw new Error(error.message || 'Failed to sign in');
  }
}

/**
 * Sign out current user
 */
export async function logOut(): Promise<void> {
  try {
    await signOut(auth);
    console.log('User signed out');
  } catch (error: any) {
    console.error('Error signing out:', error);
    throw new Error(error.message || 'Failed to sign out');
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent to:', email);
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    throw new Error(error.message || 'Failed to send password reset email');
  }
}

/**
 * Update user's password
 */
export async function changePassword(newPassword: string): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }
    
    await updatePassword(user, newPassword);
    console.log('Password updated successfully');
  } catch (error: any) {
    console.error('Error updating password:', error);
    throw new Error(error.message || 'Failed to update password');
  }
}

/**
 * Update user's username
 */
export async function changeUsername(newUsername: string): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }
    
    // Check if username is already taken by another user
    const usernameTaken = await isUsernameTaken(newUsername, user.uid);
    if (usernameTaken) {
      throw new Error('This username is already taken. Please choose a different username.');
    }
    
    // Update display name in auth
    await updateProfile(user, {
      displayName: newUsername,
    });
    
    // Update username in Firestore
    const userDocRef = doc(db, USERS_COLLECTION, user.uid);
    await updateDoc(userDocRef, {
      username: newUsername,
      displayName: newUsername,
    });
    
    console.log('Username updated successfully');
  } catch (error: any) {
    console.error('Error updating username:', error);
    throw new Error(error.message || 'Failed to update username');
  }
}

/**
 * Convert Firebase user to our User type
 */
export async function convertFirebaseUser(firebaseUser: FirebaseUser): Promise<User> {
  try {
    // Try to get user data from Firestore
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, firebaseUser.uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        id: firebaseUser.uid,
        username: userData.username || firebaseUser.displayName || 'User',
        email: firebaseUser.email || undefined,
        displayName: userData.displayName || firebaseUser.displayName,
        createdAt: userData.createdAt || Date.now(),
        events: [],
      };
    } else {
      // Create basic user from Firebase auth data
      return {
        id: firebaseUser.uid,
        username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        email: firebaseUser.email || undefined,
        displayName: firebaseUser.displayName || undefined,
        createdAt: Date.now(),
        events: [],
      };
    }
  } catch (error) {
    console.error('Error converting Firebase user:', error);
    // Return basic user on error
    return {
      id: firebaseUser.uid,
      username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
      email: firebaseUser.email || undefined,
      displayName: firebaseUser.displayName || undefined,
      createdAt: Date.now(),
      events: [],
    };
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const user = await convertFirebaseUser(firebaseUser);
      callback(user);
    } else {
      callback(null);
    }
  });
}

/**
 * Get current Firebase auth user
 */
export function getCurrentFirebaseUser(): FirebaseUser | null {
  return auth.currentUser;
}

/**
 * Check if user is signed in
 */
export function isSignedIn(): boolean {
  return auth.currentUser !== null;
}

